import { Prescription } from '../../interfaces/prescription';
import { PrescriberConfiguration } from '../prescriber';
import { Medicine, ManualMedicine } from '../../interfaces/medicine';
import { FormulaSummary } from '../../interfaces/formula';
import { Exam, Cid } from '../../interfaces/exam';
import { MedicalCertificate, MEDICAL_CERTIFICATE_DOCUMENT_TYPE } from '../../interfaces/medicalCertificate';
import { MedicalReferral } from '../../interfaces/MedicalReferral';
import { MedicalReport } from '../../interfaces/medicalReport';
import { PrescriptionCompletedPayload } from './types/prescription-completed-payload';
import { PrescriptionCompletedMedication } from './types/prescription-completed-medication';
import { PrescriptionCompletedCompoundedMedication } from './types/prescription-completed-compounded-medication';
import { PrescriptionCompletedMedicalTest } from './types/prescription-completed-medical-test';
import { PrescriptionCompletedDoctorsNote } from './types/prescription-completed-doctors-note';
import { PrescriptionCompletedReferral } from './types/prescription-completed-referral';
import { PrescriptionCompletedMedicalReport } from './types/prescription-completed-medical-report';
import { CIDItem } from './types/cid-item';
import { getConcomitanceMap, ConcomitanceOriginData, getSuggestedPosologyMap, SuggestedPosologyData, getStructuredPosologyMap, getProtocolMap, getSelectedLaboratoryMap, SelectedLaboratoryData } from './amplitudeEventStorage';

function mapMedicine(
  medicine: Medicine,
  concomitanceMap: Record<number, ConcomitanceOriginData>,
  suggestedPosologyMap: Record<number, SuggestedPosologyData>,
  structuredPosologyMap: Record<number, boolean>,
  selectedLaboratoryMap: Record<number, SelectedLaboratoryData>
): PrescriptionCompletedMedication {
  const concomitanceData = concomitanceMap[medicine.IdMedicamento];
  const hasConcomitance = concomitanceData?.hasConcomitance ?? medicine.hasConcomitance ?? false;
  const originId = concomitanceData?.originId ?? medicine.ConcomitanceMedicines?.[0]?.IdMedicamento ?? 0;
  const originName = concomitanceData?.originName ?? medicine.ConcomitanceMedicines?.[0]?.Nome ?? '';

  const posologyData = suggestedPosologyMap[medicine.IdMedicamento];
  const isSuggestedPosology = posologyData?.isSuggestedPosology ?? medicine.posologySelectedFromSuggestion ?? false;

  const isStructuredPosology =
    structuredPosologyMap[medicine.IdMedicamento] ??
    medicine.isStructuredPosology ??
    !!medicine.PosologiaEstruturada;

  const selectedLaboratoryData = selectedLaboratoryMap[medicine.IdMedicamento];

  return {
    id: medicine.IdMedicamento ?? 0,
    name: medicine.Nome ?? '',
    laboratory: medicine.Laboratorio ?? '',
    selected_laboratory: selectedLaboratoryData?.laboratory ?? '',
    active_ingredient: medicine.PrincipiosAtivos?.[0]?.Nome ?? '',
    quantity: medicine.Quantidade ?? 0,
    quantity_type: medicine.QuantidadeUnidadeFormatada ?? '',
    posology: medicine.Posologia ?? '',
    dosage_form: medicine.FormaFarmaceutica ?? '',
    is_manual: false,
    is_LME: medicine.LME ?? false,
    is_controlled: medicine.ControleEspecial ?? false,
    is_continues_use: medicine.UsoContinuo ?? false,
    is_sponsored: medicine.isPremiumPlacement ?? false,
    is_voice_prescribed: medicine.AdicionadoPorIA ?? false,
    is_suggested_posology: isSuggestedPosology,
    is_structured_posology: isStructuredPosology,
    is_concomitance_added: hasConcomitance,
    concomitance_added_origin_id: originId,
    concomitance_added_origin_name: originName,
    is_psp: medicine.Beneficios?.some(b => b.Tipo === 'INDUSTRIA') ?? false,
    is_scheduled_renewal: false, 
    scheduled_renewal: JSON.stringify([]) 
  };
}

function mapManualMedicine(medicine: ManualMedicine): PrescriptionCompletedMedication {
  return {
    id: 0,
    name: medicine.Nome ?? '',
    laboratory: '',
    selected_laboratory: '',
    active_ingredient: '',
    quantity: medicine.Quantidade ?? 0,
    quantity_type: medicine.QuantidadeUnidadeFormatada ?? '',
    posology: medicine.Posologia ?? '',
    dosage_form: '',
    is_manual: true,
    is_LME: medicine.LME ?? false,
    is_controlled: false,
    is_continues_use: medicine.UsoContinuo ?? false,
    is_sponsored: false,
    is_voice_prescribed: medicine.AdicionadoPorIA ?? false,
    is_suggested_posology: false,
    is_structured_posology: false,
    is_concomitance_added: false,
    concomitance_added_origin_id: 0,
    concomitance_added_origin_name: '',
    is_psp: false,
    is_scheduled_renewal: false,
    scheduled_renewal: JSON.stringify([]),
  };
}

function mapFormula(formula: FormulaSummary): PrescriptionCompletedCompoundedMedication {
  return {
    id: formula.id ?? 0,
    name: formula.NomeFormula ?? '',
    quantity: formula.Quantidade ?? 0,
    quantity_type: formula.SimboloMedida ?? '',
    posology: formula.Posologia ?? '',
    dosage_form: formula.NomeFormaFisica ?? '',
    units_of_measurement: formula.SimboloMedida ?? '',
    composition: !!formula.Composicao,
    prescription_type: formula.TipoReceita ?? '',
  };
}

function mapExam(exam: Exam): PrescriptionCompletedMedicalTest {
  const firstCid = exam.CID10Estruturado?.[0]; // todo: um exame pode ter vários CIDs, confirmar se não querem array aqui
  return {
    id: exam.idPrescricaoExame ?? 0,
    name: exam.Nome ?? '',
    TUSS: exam.CodigoTUSS ?? '',
    clinical_indication: exam.ObservacaoEspecifica ? (exam.Instrucoes ?? '') : '',
    APAC: exam.tipoFormulario?.Chave === 'FORMULARIO_APAC',
    due: exam.QuandoRealizar ?? '',
    quantity: exam.QuantidadeSessoes ?? 0,
    is_voice_prescribed: exam.AdicionadoPorIA ?? false,
    CID: firstCid ? { code: firstCid.Codigo, name: firstCid.Descricao } : { code: '', name: '' },
  };
}

function mapMedicalCertificate(cert: MedicalCertificate): PrescriptionCompletedDoctorsNote {
  const cidArray = cert.CID10Estruturado?.map(c => ({ code: c.Codigo, name: c.Descricao })) ?? [];
  return {
    CID: JSON.stringify(cidArray),
    date: cert.DataAtestado ?? '',
    start_date: cert.DataInicio ?? '',
    end_date: cert.DataTermino ?? '',
    is_attendance: cert.DeclaracaoComparecimento ?? false,
    is_attendance_for_companion: cert.Tipo === MEDICAL_CERTIFICATE_DOCUMENT_TYPE.DECLARATION_OF_COMPANION,
    medical_reason: cert.Texto ?? '',
    notes: cert.Observacao ?? '',
    days_of_rest: cert.Periodo ?? '',
    days_of_rest_type: cert.TipoPeriodo ?? '',
    is_voice_prescribed: cert.AdicionadoPorIA ?? false,
  };
}

function mapMedicalReferral(referral: MedicalReferral): PrescriptionCompletedReferral {
  const cidArray: CIDItem[] = referral.CID10
    ? [{ code: referral.CID10.SubCategoria, name: referral.CID10.Descricao }]
    : [];

  return {
    id: referral.idEncaminhamentoPrescricao ?? 0,
    CID: JSON.stringify(cidArray),
    specialty: referral.Especialidade?.Nome ?? '',
    date: referral.DataEncaminhamento instanceof Date
      ? referral.DataEncaminhamento.toISOString()
      : new Date(referral.DataEncaminhamento).toISOString(),
    medical_reason: referral.Motivo ?? '',
    clinical_history: referral.HistoriaClinica ?? '',
    clinical_conduct: referral.Conduta ?? '',
    is_voice_prescribed: referral.AdicionadoPorIA ?? false,
  };
}

function mapMedicalReport(report: MedicalReport): PrescriptionCompletedMedicalReport {
  return {
    id: report.idPrescricaoLaudoMedico ?? 0,
    document: report.Titulo ?? '',
    description: report.Texto ?? '',
    date: report.DataLaudoMedico ?? '',
  };
}

export function buildPrescriptionCompletedPayload(
  prescription: Prescription,
  examStore: { generalExamIcd: Cid | null; generalExamObservation: string },
  prescriberConfiguration?: PrescriberConfiguration | null
): PrescriptionCompletedPayload {
  const concomitanceMap = getConcomitanceMap(prescription.idPrescricao);
  const suggestedPosologyMap = getSuggestedPosologyMap(prescription.idPrescricao);
  const structuredPosologyMap = getStructuredPosologyMap(prescription.idPrescricao);
  const protocolMap = getProtocolMap(prescription.idPrescricao);
  const selectedLaboratoryMap = getSelectedLaboratoryMap(prescription.idPrescricao);

  const medications = [
    ...(prescription.Itens ?? []).map((m) => mapMedicine(m, concomitanceMap, suggestedPosologyMap, structuredPosologyMap, selectedLaboratoryMap)),
    ...(prescription.ItensManuais ?? []).map(mapManualMedicine),
  ];

  const medicalTestsCID: CIDItem[] = examStore.generalExamIcd
    ? [{ code: examStore.generalExamIcd.Codigo, name: examStore.generalExamIcd.Descricao }]
    : [];

  return {
    validation_code: prescription.CodigoValidacao ?? '',
    partner_id: prescriberConfiguration?.idParceiro ?? 0,
    partner_name: prescriberConfiguration?.Nome ?? '',
    establishment_name: prescription.Estabelecimento?.Nome ?? '',
    notes: prescription.Comentario ?? '',
    is_generic_name_activated: prescription.ExibirPorPrincipioAtivo ?? false,
    medications,
    compounded_medication: (prescription.Formulas ?? []).map((f) => ({ id: f.id })),
    compounded_medications: ((prescription.Formulas ?? []) as unknown as FormulaSummary[]).map(mapFormula),
    medical_tests: (prescription.Exames ?? []).map(mapExam),
    medical_tests_CID: medicalTestsCID,
    medical_tests_clinical_indication: examStore.generalExamObservation ?? '',
    doctors_note: (prescription.Atestados ?? []).map(mapMedicalCertificate),
    referral: (prescription.Encaminhamentos ?? []).map(mapMedicalReferral),
    medical_report: (prescription.Relatorios ?? []).map(mapMedicalReport),
    protocol: Object.values(protocolMap),
  };
}
